import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone, Plus, Trash2 } from 'lucide-react';
import { WAITLIST_STATUS_LABELS } from '@saesak/shared';
import type { WaitlistStatus } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatDate, formatFullDate, formatPhone, formatPhoneInput } from '@/lib/format';

interface WaitlistRow {
  id: string;
  childName: string;
  birthDate: string | null;
  guardianName: string;
  guardianPhone: string;
  memo: string | null;
  status: WaitlistStatus;
  appliedAt: string;
  waitingOrder: number | null;
}

const STATUS_TONE = {
  WAITING: 'primary',
  OFFERED: 'accent',
  ENROLLED: 'secondary',
  CANCELED: 'neutral',
} as const;

/** 다음 단계로 보낼 수 있는 상태 전이 — 대기 → 안내 → 입소, 어느 단계든 취소 가능 */
const NEXT_STATUS: Partial<Record<WaitlistStatus, WaitlistStatus[]>> = {
  WAITING: ['OFFERED', 'CANCELED'],
  OFFERED: ['ENROLLED', 'CANCELED'],
};

/**
 * 입소 대기 관리 — P5 후순위 모듈 (원장 전용, 최소 구현)
 * 전화·방문 문의를 접수일 순번으로 관리한다. 셀프 신청 연동은 범위 밖.
 */
export function WaitlistPage() {
  const [composing, setComposing] = useState(false);
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['waitlist'],
    queryFn: () => apiData<WaitlistRow[]>('/waitlist'),
  });

  const rows = data ?? [];
  const waiting = rows.filter((row) => row.status === 'WAITING').length;

  return (
    <Screen>
      <AppBar title="입소 대기" back />

      <div className="flex flex-col gap-3 p-4">
        {!composing && (
          <Button variant="outline" icon={<Plus size={16} />} onClick={() => setComposing(true)}>
            대기 등록
          </Button>
        )}
        {composing && <WaitlistForm onClose={() => setComposing(false)} />}

        {isPending && <Spinner />}
        {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

        {!isPending && rows.length === 0 && !composing && (
          <EmptyState
            title="대기 중인 신청이 없어요"
            body="입소 문의가 오면 여기에서 접수 순서대로 관리할 수 있어요."
          />
        )}

        {rows.length > 0 && (
          <p className="px-1 text-sm text-ink-sub">
            대기 중 <b className="tnum">{waiting}</b>명 / 전체 <b className="tnum">{rows.length}</b>건
          </p>
        )}

        {rows.map((row) => (
          <WaitlistCard key={row.id} row={row} />
        ))}
      </div>
    </Screen>
  );
}

function WaitlistCard({ row }: { row: WaitlistRow }) {
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['waitlist'] });

  const update = useMutation({
    mutationFn: (status: WaitlistStatus) =>
      apiData(`/waitlist/${row.id}`, { method: 'PATCH', body: { status } }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => apiData(`/waitlist/${row.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  return (
    <Card>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {row.waitingOrder != null && (
              <span className="tnum mr-1.5 text-primary-strong">{row.waitingOrder}번</span>
            )}
            {row.childName}
            {row.birthDate && (
              <span className="ml-1.5 text-sm font-normal text-ink-sub">
                {formatFullDate(row.birthDate)}생
              </span>
            )}
          </p>
          <p className="mt-0.5 flex items-center gap-1 text-sm text-ink-sub">
            {row.guardianName} <Phone size={12} /> {formatPhone(row.guardianPhone)}
          </p>
          <p className="mt-0.5 text-sm text-ink-sub">접수 {formatDate(row.appliedAt)}</p>
          {row.memo && <p className="mt-1 whitespace-pre-wrap text-sm">{row.memo}</p>}
        </div>
        <Chip tone={STATUS_TONE[row.status]}>{WAITLIST_STATUS_LABELS[row.status]}</Chip>
      </div>

      <div className="mt-2 flex items-center gap-2">
        {(NEXT_STATUS[row.status] ?? []).map((status) => (
          <Button
            key={status}
            size="sm"
            variant={status === 'CANCELED' ? 'outline' : 'primary'}
            loading={update.isPending}
            onClick={() => update.mutate(status)}
          >
            {WAITLIST_STATUS_LABELS[status]}
          </Button>
        ))}
        <button
          type="button"
          aria-label="삭제"
          className="tap ml-auto grid h-9 w-9 place-items-center rounded-sm text-ink-sub hover:text-notice"
          onClick={() => {
            if (window.confirm(`${row.childName} 대기 신청을 삭제할까요?`)) remove.mutate();
          }}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </Card>
  );
}

function WaitlistForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [childName, setChildName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiData('/waitlist', {
        method: 'POST',
        body: {
          childName,
          birthDate: birthDate || undefined,
          guardianName,
          guardianPhone,
          memo: memo || undefined,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '등록하지 못했어요.'),
  });

  return (
    <Card>
      <p className="mb-2 font-semibold">대기 등록</p>
      <div className="flex flex-col gap-3">
        <Field label="아이 이름" required>
          <Input value={childName} onChange={(e) => setChildName(e.target.value)} />
        </Field>
        <Field label="생년월일">
          <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
        </Field>
        <Field label="보호자 이름" required>
          <Input value={guardianName} onChange={(e) => setGuardianName(e.target.value)} />
        </Field>
        <Field label="보호자 연락처" required>
          <Input
            inputMode="tel"
            value={guardianPhone}
            onChange={(e) => setGuardianPhone(formatPhoneInput(e.target.value))}
            placeholder="010-0000-0000"
          />
        </Field>
        <Field label="메모">
          <Textarea
            rows={2}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="희망 시기, 형제 재원 여부 등"
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <Button
            block
            loading={create.isPending}
            disabled={!childName.trim() || !guardianName.trim() || !guardianPhone.trim()}
            onClick={() => create.mutate()}
          >
            등록
          </Button>
          <Button block variant="outline" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>
    </Card>
  );
}
